(() => {
    'use strict';

    const NS = (window.ProductGalleryVariant = window.ProductGalleryVariant || {});
    const cache = new Map();
    const rIC = window.requestIdleCallback || ((cb) => setTimeout(() => cb({ timeRemaining: () => 16 }), 1));

    const GALLERY_SELECTOR = '[data-product-gallery]';
    const keyOf = (sectionId, variantId, mode = 'main') => `${sectionId}:${mode}:${variantId}`;

    async function fetchGalleryHTMLString({ productUrl, sectionId, sectionDomId, variantId, mode = 'main', signal }) {
        const k = keyOf(sectionId, variantId, mode);
        if (cache.has(k)) return cache.get(k);

        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', sectionId);

        const res = await fetch(url.toString(), { credentials: 'same-origin', signal });
        if (!res.ok) throw new Error(`Section render ${res.status}`);
        const html = await res.text();

        // Parse once, extract the target section, then the gallery inside it
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshSection = doc.getElementById(sectionDomId);
        if (!freshSection) throw new Error(`Could not find section #${sectionDomId} in response`);

        const freshGallery = freshSection.querySelector(GALLERY_SELECTOR);
        if (!freshGallery) throw new Error(`Could not find ${GALLERY_SELECTOR} inside #${sectionDomId}`);

        const galleryHTML = freshGallery.outerHTML;
        cache.set(k, galleryHTML);
        return galleryHTML;
    }

    function buildFromHTML(html) {
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        return tpl.content.firstElementChild;
    }

    async function decodeImages(root) {
        const imgs = Array.from(root.querySelectorAll('img'));
        if (!imgs.length) return;
        const hero = imgs[0];
        if (hero) {
            try { hero.fetchPriority = 'high'; } catch (_) {}
            hero.loading = 'eager';
            if (hero.dataset?.src && !hero.src) hero.src = hero.dataset.src;
            if (hero.dataset?.srcset && !hero.srcset) hero.srcset = hero.dataset.srcset;
        }
        await Promise.all(
            imgs.map((img) => {
                if (img.dataset?.src && !img.src) img.src = img.dataset.src;
                if (img.dataset?.srcset && !img.srcset) img.srcset = img.dataset.srcset;
                return img.decode?.().catch(() => {});
            }),
        );
    }

    async function smoothReplace(oldNode, newNode, doReplace) {
        const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
        const canVT = !reduce && typeof document.startViewTransition === 'function';
        if (canVT) {
            await document.startViewTransition(() => {
                doReplace();
            }).finished;
            return;
        }
        const h = oldNode.getBoundingClientRect().height;
        const prev = {
            minHeight: oldNode.style.minHeight,
            pos: oldNode.style.position,
            opacity: oldNode.style.opacity,
            transition: oldNode.style.transition,
        };
        oldNode.style.minHeight = h + 'px';
        oldNode.style.position = 'relative';
        oldNode.style.opacity = '0.001';
        doReplace();
        requestAnimationFrame(() => {
            const live = oldNode.parentNode ? oldNode : newNode;
            live.style.transition = 'opacity 180ms ease';
            live.style.opacity = '1';
            setTimeout(() => {
                live.style.minHeight = prev.minHeight || '';
                live.style.position = prev.pos || '';
                live.style.opacity = prev.opacity || '';
                live.style.transition = prev.transition || '';
            }, 220);
        });
    }

    function init(sectionEl) {
        if (!sectionEl || sectionEl.__pgvInitialized) return;
        sectionEl.__pgvInitialized = true;

        let sectionDomId = sectionEl.id;
        const sectionId = sectionEl.dataset.sectionId;
        const mainProductUrl = sectionEl.dataset.productUrl || window.location.pathname;
        const subProductUrl = sectionEl.dataset.subProductUrl || null;
        const historyOn = sectionEl.dataset.enableHistoryState === 'true';

        // ---- read main product JSON ----
        let mainProductData = null;
        const productJsonEl = sectionEl.querySelector('[data-product-json]');
        if (productJsonEl?.textContent) {
            try {
                mainProductData = JSON.parse(productJsonEl.textContent);
            } catch (e) {
                console.error('[pgv] Failed to parse main product JSON', e);
            }
        }

        // ---- read tied/subscription product JSON ----
        let subProductData = null;
        const subJsonEl = sectionEl.querySelector('[data-sub-product-json]');
        if (subJsonEl?.textContent) {
            try {
                subProductData = JSON.parse(subJsonEl.textContent);
            } catch (e) {
                console.error('[pgv] Failed to parse sub product JSON', e);
            }
        }

        function getMainVariantById(id) {
            if (!mainProductData || !Array.isArray(mainProductData.variants)) return null;
            const sid = String(id);
            return mainProductData.variants.find((v) => String(v.id) === sid) || null;
        }

        // match sub variant by options (assumes same option structure and order)
        function findSubVariantFor(mainVariantId) {
            if (!subProductData || !Array.isArray(subProductData.variants)) return null;
            const mainVar = getMainVariantById(mainVariantId);
            if (!mainVar) return null;

            return (
                subProductData.variants.find(
                    (v) =>
                        v.option1 === mainVar.option1 &&
                        v.option2 === mainVar.option2 &&
                        v.option3 === mainVar.option3,
                ) || null
            );
        }

        // Skio: selling plan selected? (subscription vs one-time)
        function isSubscriptionSelected() {
            const spInput = sectionEl.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
            return !!(spInput && spInput.value);
        }

        let lastVariantId =
            new URL(window.location.href).searchParams.get('variant') ||
            sectionEl.querySelector('input[name="id"]')?.value ||
            null;

        let isRendering = false;
        let inFlight = null;

        function fireGalleryLifecycle(galleryEl) {
            galleryEl.dispatchEvent(new Event('theme:gallery:load', { bubbles: true }));
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        }

        function bindIdInput() {
            const idInput = sectionEl.querySelector('input[name="id"]');
            if (!idInput) return;
            idInput.addEventListener('change', () => renderVariant(String(idInput.value)));
        }
        bindIdInput();

        // Re-render gallery when Skio selling plan changes (subscription / one-time switch)
        function bindSellingPlanChange() {
            sectionEl.addEventListener('change', (e) => {
                const t = e.target;
                if (!t.matches('input[name="selling_plan"], select[name="selling_plan"]')) return;
                const vid = sectionEl.querySelector('input[name="id"]')?.value;
                if (vid) renderVariant(String(vid), { force: true });
            });
        }
        bindSellingPlanChange();

        async function swapGalleryOnly(galleryHTML) {
            const freshGallery = buildFromHTML(galleryHTML);
            await decodeImages(freshGallery);

            const liveGallery = sectionEl.querySelector(GALLERY_SELECTOR);
            if (!liveGallery) {
                const url = new URL(mainProductUrl, window.location.origin);
                const vid = lastVariantId || sectionEl.querySelector('input[name="id"]')?.value;
                url.searchParams.set('variant', vid || '');
                url.searchParams.set('section_id', sectionId);
                const res = await fetch(url.toString(), { credentials: 'same-origin' });
                const html = await res.text();
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const freshSection = doc.getElementById(sectionDomId);
                if (freshSection) {
                    await decodeImages(freshSection);
                    await smoothReplace(sectionEl, freshSection, () => sectionEl.replaceWith(freshSection));
                    sectionEl = freshSection;
                    sectionDomId = sectionEl.id || sectionDomId;
                    bindIdInput();
                    bindSellingPlanChange();
                }
                return;
            }

            await smoothReplace(liveGallery, freshGallery, () => {
                liveGallery.replaceWith(freshGallery);
            });
            fireGalleryLifecycle(freshGallery);
            
            freshGallery.dataset.galleryReady = 'true';
            freshGallery.removeAttribute('data-hide-until-ready');
        }

        async function actuallyRenderVariant(mainVariantId) {
            if (!mainVariantId || isRendering) return;

            const subscriptionOn = isSubscriptionSelected();
            const hasSubProduct = !!(subProductUrl && subProductData);
            const mode = subscriptionOn && hasSubProduct ? 'sub' : 'main';

            // avoid unnecessary refresh of the same variant in main mode
            if (mainVariantId === lastVariantId && mode === 'main') return;

            isRendering = true;
            if (inFlight) inFlight.abort();
            inFlight = new AbortController();

            let targetVariantId = mainVariantId;
            let productUrlToUse = mainProductUrl;

            if (mode === 'sub') {
                const subVar = findSubVariantFor(mainVariantId);
                if (subVar) {
                    targetVariantId = String(subVar.id);
                }
                productUrlToUse = subProductUrl;
            }

            try {
                const galleryHTML = await fetchGalleryHTMLString({
                    productUrl: productUrlToUse,
                    sectionId,
                    sectionDomId,
                    variantId: String(targetVariantId),
                    mode,
                    signal: inFlight.signal,
                });
                await swapGalleryOnly(galleryHTML);

                // track main variant id for history / state
                lastVariantId = String(mainVariantId);
                if (historyOn) {
                    const u = new URL(window.location.href);
                    u.searchParams.set('variant', mainVariantId);
                    window.history.replaceState({}, '', u.toString());
                }
            } catch (e) {
                if (e.name !== 'AbortError') console.error('[pgv]', e);
            } finally {
                isRendering = false;
                inFlight = null;
            }
        }

        let t;
        const renderVariant = (idRaw, opts = {}) => {
            const id = String(idRaw);
            clearTimeout(t);

            // If we're forcing (e.g. selling plan changed), render immediately
            if (opts.force) {
                actuallyRenderVariant(id);
                return;
            }

            // modest debounce to coalesce noisy emitters
            t = setTimeout(() => actuallyRenderVariant(id), 40);
        };

        function readVariantIds(sec) {
            const el = sec.querySelector('[data-product-json]');
            if (!el?.textContent) return [];
            try {
                const p = JSON.parse(el.textContent);
                return (p?.variants || []).map((v) => String(v.id));
            } catch {
                return [];
            }
        }
        const allVariantIds = readVariantIds(sectionEl);
        rIC(() => {
            const cur = sectionEl.querySelector('input[name="id"]')?.value;
            const candidates = allVariantIds.filter((v) => v !== cur);
            candidates.slice(0, 8).forEach((vid, i) => {
                rIC(
                    async () => {
                        try {
                            await fetchGalleryHTMLString({
                                productUrl: mainProductUrl,
                                sectionId,
                                sectionDomId,
                                variantId: vid,
                                mode: 'main',
                            });
                        } catch {}
                    },
                    { timeout: 1500 + i * 120 },
                );
            });
        });

        // Hover prefetch for radios / swatches (main product only – sub prefetched on demand)
        document.addEventListener(
            'mouseover',
            (e) => {
                const input = e.target?.closest?.('input[type="radio"][data-single-option-selector]');
                if (!input) return;
                const vid = String(input.dataset.variantId || input.value || '');
                if (!vid) return;
                const k = keyOf(sectionId, vid, 'main');
                if (cache.has(k)) return;
                rIC(async () => {
                    try {
                        await fetchGalleryHTMLString({
                            productUrl: mainProductUrl,
                            sectionId,
                            sectionDomId,
                            variantId: vid,
                            mode: 'main',
                        });
                    } catch {}
                });
            },
            { passive: true },
        );

        const handleVariantChange = (vid) => vid && renderVariant(String(vid));
        document.addEventListener('variantChanged', (e) => handleVariantChange(e?.detail?.variantId));
        document.addEventListener('variant:change', (e) =>
            handleVariantChange(e?.detail?.variant?.id || e?.detail?.variantId),
        );
        document.addEventListener('product:variant-change', (e) =>
            handleVariantChange(e?.detail?.variant?.id || e?.detail?.variantId),
        );

        const nowId = sectionEl.querySelector('input[name="id"]')?.value;
        const liveGallery = sectionEl.querySelector(GALLERY_SELECTOR);

        if (nowId && liveGallery) {
            lastVariantId = String(nowId);
            const seedKey = keyOf(sectionId, String(nowId), 'main');
            if (!cache.has(seedKey)) cache.set(seedKey, liveGallery.outerHTML);

            const subscriptionOn = isSubscriptionSelected();
            const hasSubProduct = !!(subProductUrl && subProductData);
            const hideUntilReady = liveGallery.hasAttribute('data-hide-until-ready');

            if (hideUntilReady && subscriptionOn && hasSubProduct) {
                // Subscription is default: keep hidden and immediately swap to the tied product gallery
                renderVariant(String(nowId), { force: true });
            } else {
                // Either one-time is default OR there is no tied product:
                // just show the current gallery immediately.
                liveGallery.dataset.galleryReady = 'true';
                liveGallery.removeAttribute('data-hide-until-ready');
            }
        }

        NS.renderVariant = (id) => renderVariant(String(id));


    }

    function boot(root = document) {
        root.querySelectorAll('[data-product-gallery-dynamic="true"]').forEach(init);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => boot(document));
    } else {
        boot(document);
    }
    document.addEventListener('shopify:section:load', (e) => boot(e.target || document));
    document.addEventListener('shopify:section:select', (e) => boot(e.target || document));

    window.ProductGalleryVariant = { ...(window.ProductGalleryVariant || {}), init, boot };
})();
